/**
 * ResumePreviewButton — drop-in icon button that opens a resume preview
 * dialog for the given candidate. Bundles its own state so callers don't
 * need to lift `previewOpen` up.
 *
 * Usage:
 *   <ResumePreviewButton candidate={c} testId={`preview-${c.id}`} />
 *
 * Screening-mode usage (Prev/Next cycle without leaving the dialog):
 *   <ResumePreviewButton
 *      candidate={row.candidate}
 *      siblings={sortedRows}      // same application-row shape as `row`
 *      stages={stages}
 *      onStageChange={(appId, key) => updateStage(appId, key)}
 *   />
 *
 * Silently disables (opacity, no click) when the candidate has no resume
 * on file — avoids the confusing dead-end of a button that opens an empty
 * dialog. Hover title explains why.
 */
import { useState, useMemo, useEffect } from "react";
import ResumePreviewDialog from "@/components/ResumePreviewDialog";
import { FileText } from "lucide-react";

export default function ResumePreviewButton({
    candidate,
    testId,
    className = "",
    // Screening-mode (optional):
    siblings,       // array of application rows with .candidate + .id + .stage + .vote_summary
    stages,
    onStageChange,
    onOpenProfile,  // callback to open the standalone candidate profile dialog
}) {
    const [open, setOpen] = useState(false);
    // When navigation is active, the dialog cycles by advancing this index.
    const initialIndex = useMemo(() => {
        if (!Array.isArray(siblings) || !candidate?.id) return 0;
        const i = siblings.findIndex((s) => (s?.candidate?.id || s?.candidate_id) === candidate.id);
        return i >= 0 ? i : 0;
    }, [siblings, candidate?.id]);
    const [index, setIndex] = useState(initialIndex);

    // Re-sync when the owning row changes (parent re-renders different candidate).
    useEffect(() => { setIndex(initialIndex); }, [initialIndex]);

    if (!candidate) return null;
    const hasResume = !!candidate.resume_filename;
    const useScreeningMode = Array.isArray(siblings) && siblings.length > 1;

    return (
        <>
            <button
                type="button"
                disabled={!hasResume}
                onClick={(e) => {
                    e.stopPropagation();
                    if (hasResume) {
                        // Snap any open hovercards shut before the preview dialog mounts.
                        try { window.dispatchEvent(new Event("pw-hovercard-close")); } catch { /* ignore */ }
                        setIndex(initialIndex);
                        setOpen(true);
                    }
                }}
                className={`inline-flex items-center justify-center w-6 h-6 border transition-colors ${
                    hasResume
                        ? "border-ikb/40 text-ikb hover:bg-ikb hover:text-white hover:border-ikb"
                        : "border-border text-muted-foreground opacity-40 cursor-not-allowed"
                } ${className}`}
                data-testid={testId || `resume-preview-${candidate.id}`}
                aria-label="Preview resume"
                title={hasResume ? "Preview resume" : "No resume on file"}
            >
                <FileText className="w-3 h-3" />
            </button>
            {open && (
                <ResumePreviewDialog
                    open={open}
                    onClose={() => setOpen(false)}
                    candidate={useScreeningMode ? undefined : candidate}
                    entries={useScreeningMode ? siblings : undefined}
                    entryIndex={useScreeningMode ? index : undefined}
                    onNavigate={useScreeningMode ? setIndex : undefined}
                    stages={stages}
                    onStageChange={onStageChange}
                    onOpenProfile={onOpenProfile ? (args) => {
                        setOpen(false);
                        onOpenProfile(args);
                    } : undefined}
                />
            )}
        </>
    );
}
